import { computed, h, ref, watch, type ComputedRef, type Ref, type VNode } from 'vue'
import type { TreeOption } from 'naive-ui'
import type { WikiPage } from '../stores/wiki_pages'
import { categoryLabel } from './categoryLabel'
import { GENERAL_BUCKET_ALIAS, isMemoryPage, isPersonPage, PERSON_BUCKET_LABEL } from './categoryBucket'

// Owns the catalog-tree shape that WikiSidebar feeds to NTree:
//   - groups pages by category, sorts within each
//   - builds the {key, label, children} option tree NTree consumes,
//     marking categories non-selectable and pages as leaves
//   - mirrors the host's `selectedId` into NTree's array-shape
//     `selected-keys` prop
//   - manages `expandedKeys`, including auto-expand on first load
//     (always "General" + any small category) and per-page
//     auto-expand as the host navigates
//   - exposes a parser that turns NTree's `update:selected-keys`
//     payload into the page id the host should emit (host owns
//     emission so the composable stays free of Vue-emit semantics)
//   - exposes a render function that stamps catalog-page /
//     catalog-category data-testids onto each row so e2e selectors
//     keep matching
//
// Pulled out of WikiSidebar so the view stays focused on
// orchestration (status branches, search vs catalog mode, drag
// handle wiring) and the catalog-tree concerns can evolve in one
// place with their own spec.

const CATEGORY_PREFIX = 'cat:'
const PAGE_PREFIX = 'page:'

// Auto-expand small categories on first load so every page in them
// is reachable with one tap. Larger categories stay collapsed to
// keep the 288px sidebar from turning into a long scrolling list.
const AUTO_EXPAND_MAX_PAGES = 8

export interface CatalogTree {
  treeOptions: ComputedRef<TreeOption[]>
  selectedKeys: ComputedRef<string[]>
  expandedKeys: Ref<string[]>
  parseSelection: (keys: Array<string | number>) => number | null
  renderLabel: (info: { option: TreeOption }) => VNode
  // Returned for NTree's `override-default-node-click-behavior` prop.
  // Forces category-row clicks to toggle expansion regardless of
  // NTree's per-option `selectable: false` interaction with
  // `block-line`.
  rowClickBehavior: (info: { option: TreeOption }) => 'toggleExpand' | 'default'
}

export function useCatalogTree(
  pages: Ref<readonly WikiPage[]>,
  selectedId: Ref<number | null>,
  isMobile: Ref<boolean>,
): CatalogTree {
  const expandedKeys = ref<string[]>([])

  // [displayCategory, sortedPages][] — preserved order:
  // "General" (the catch-all for top-level pages) always first,
  // remaining categories alphabetical so the menu reads predictably.
  const groupedPages = computed(() => {
    const groups: Record<string, WikiPage[]> = {}
    for (const page of pages.value) {
      // Memory has its own dedicated /memory destination; skip it
      // here so the tree doesn't render a duplicate "Memory" group
      // alongside the pinned Memory link (and the noisy per-project
      // MEMORY.md index pages it would pull in).
      if (isMemoryPage(page)) continue
      const cat = isPersonPage(page) ? PERSON_BUCKET_LABEL : categoryLabel(page.category || GENERAL_BUCKET_ALIAS)
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(page)
    }
    for (const cat of Object.keys(groups)) {
      groups[cat].sort((a, b) => (a.title || a.path).localeCompare(b.title || b.path))
    }
    return Object.entries(groups).toSorted(([a], [b]) => {
      if (a === 'General') return -1
      if (b === 'General') return 1
      return a.localeCompare(b)
    })
  })

  // Per-option `selectable: false` on the category headers so
  // clicking a category expands/collapses without selecting it; only
  // page leaves emit a selection. Pages get `isLeaf: true` so the
  // tree renders them without a chevron.
  const treeOptions = computed<TreeOption[]>(() =>
    groupedPages.value.map(([cat, list]) => ({
      label: cat,
      key: `${CATEGORY_PREFIX}${cat}`,
      selectable: false,
      children: list.map((p) => ({
        label: p.title || p.path,
        key: `${PAGE_PREFIX}${p.id}`,
        isLeaf: true,
      })),
    })),
  )

  // NTree's `selected-keys` is an array; the host has at most one
  // selection at a time. Wrap the single id so the prop binding
  // reads cleanly.
  const selectedKeys = computed<string[]>(() => (selectedId.value == null ? [] : [`${PAGE_PREFIX}${selectedId.value}`]))

  // Keep the selected page's category expanded as the host
  // navigates. On mobile the tree is non-accordion (the sidebar
  // fills the viewport, so multiple expanded categories are
  // useful); on desktop we replace the expanded set with just the
  // newly-selected page's category to keep the 288px column tidy.
  function expandCategoryForPage(pageId: number | null): void {
    if (pageId == null) return
    for (const [cat, list] of groupedPages.value) {
      if (list.some((p) => p.id === pageId)) {
        const key = `${CATEGORY_PREFIX}${cat}`
        if (!expandedKeys.value.includes(key)) {
          expandedKeys.value = isMobile.value ? [...expandedKeys.value, key] : [key]
        }
        return
      }
    }
  }
  watch(selectedId, expandCategoryForPage, { immediate: true })

  // First-load auto-expand: always "General" plus any category
  // that's under the AUTO_EXPAND_MAX_PAGES threshold. Larger
  // categories stay collapsed. Skipped after the first non-empty
  // computation so the user's manual expand/collapse choices stick.
  watch(
    treeOptions,
    (options) => {
      if (expandedKeys.value.length > 0 || options.length === 0) return
      expandedKeys.value = options
        .filter((option) => option.label === 'General' || (option.children?.length ?? 0) <= AUTO_EXPAND_MAX_PAGES)
        .map((option) => String(option.key))
      if (expandedKeys.value.length === 0) {
        expandedKeys.value = [String(options[0].key)]
      }
    },
    { immediate: true },
  )

  // NTree fires update with the new selected-keys array. Returns
  // the parsed page id, or null when the selection is a category
  // header (defensive — categories are non-selectable per option,
  // but the guard keeps us safe if a future change drops that
  // per-option flag for a global one). Host emits.
  function parseSelection(keys: Array<string | number>): number | null {
    const next = keys.find((k) => typeof k === 'string' && k.startsWith(PAGE_PREFIX))
    if (typeof next !== 'string') return null
    const id = Number.parseInt(next.slice(PAGE_PREFIX.length), 10)
    return Number.isFinite(id) ? id : null
  }

  // Tag tree nodes with a testid so Playwright can distinguish
  // category headers from selectable page leaves without relying on
  // NTree internals.
  function renderLabel({ option }: { option: TreeOption }): VNode {
    const key = String(option.key ?? '')
    const isPage = key.startsWith(PAGE_PREFIX)
    return h('span', { 'data-testid': isPage ? 'catalog-page' : 'catalog-category' }, String(option.label ?? ''))
  }

  // Clicking the category row in `block-line` mode did not
  // expand/collapse — only the chevron icon worked. Naive UI's
  // default _handleClick should fire `handleSwitcherClick` for any
  // non-leaf row, but its interaction with our per-option
  // `selectable: false` flag stopped it from firing on the row body
  // in practice. Overriding the click behaviour for category rows
  // makes the contract explicit and works around the quirk: every
  // category click toggles expansion, every page click goes through
  // the default selection path.
  function rowClickBehavior({ option }: { option: TreeOption }): 'toggleExpand' | 'default' {
    const key = String(option.key ?? '')
    return key.startsWith(CATEGORY_PREFIX) ? 'toggleExpand' : 'default'
  }

  return { treeOptions, selectedKeys, expandedKeys, parseSelection, renderLabel, rowClickBehavior }
}
